import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { usePermissions } from '../../auth/usePermissions'
import { MaruyaApiError } from '../../lib/maruya-api'
import {
  displayDocumentTitle,
  sourceTypeLabel,
} from '../../lib/document-display'
import {
  approveCurationRequest,
  getDocument,
  listCurationRequests,
  listDocuments,
  rejectCurationRequest,
} from '../../lib/rag-api'
import type { CurationRequest, RagDocumentSummary } from '../../types/api'
import { IconCheck, IconChat, IconInbox, IconTrash, IconX } from '../../components/ui/Icon'
import { usePageHeader } from '../../components/layout/PageHeaderContext'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { DeleteRequestDialog } from '../../components/rag/DeleteRequestDialog'

type StatusFilter = 'all' | 'pending'

type DocumentRow = RagDocumentSummary & {
  curation: CurationRequest | null
}

type ApprovalAction = {
  documentId: string
  requestId: string
  title: string
  type: 'approve' | 'reject'
}

function rowTypeLabel(row: DocumentRow): string {
  const fromUri = sourceTypeLabel(row.source_uri)
  if (fromUri !== '出典') {
    return fromUri
  }
  if (row.source_type && row.source_type !== '—') {
    return sourceTypeLabel(`office://${row.source_type}/_`)
  }
  return '—'
}

export function DocumentsPage() {
  usePageHeader('C. 承認待ち', '承認待ち')

  const { canCurate, canApprove } = usePermissions()
  const canWorkbench = canCurate || canApprove
  const [searchParams, setSearchParams] = useSearchParams()
  const [documents, setDocuments] = useState<RagDocumentSummary[]>([])
  const [documentsLoaded, setDocumentsLoaded] = useState(false)
  const [pendingRequests, setPendingRequests] = useState<CurationRequest[]>([])
  const [nextCursor, setNextCursor] = useState<string | undefined>()
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending')
  const [deleteTarget, setDeleteTarget] = useState<DocumentRow | null>(null)
  const [approvalAction, setApprovalAction] = useState<ApprovalAction | null>(null)
  const [approvalError, setApprovalError] = useState<string | null>(null)
  const [busyDocumentId, setBusyDocumentId] = useState<string | null>(null)

  const pendingByDocumentId = useMemo(
    () => new Map(pendingRequests.map((request) => [request.document_id, request])),
    [pendingRequests],
  )

  const rows = useMemo(() => {
    const documentRows: DocumentRow[] = documents.map((document) => ({
      ...document,
      curation: pendingByDocumentId.get(document.document_id) ?? null,
    }))

    for (const request of pendingRequests) {
      if (documentRows.some((row) => row.document_id === request.document_id)) {
        continue
      }
      documentRows.push({
        document_id: request.document_id,
        source_uri: request.source_uri,
        title: displayDocumentTitle(
          request.title,
          request.source_uri,
          request.document_id,
        ),
        updated_at: request.created_at,
        source_type: request.source_type || '—',
        curation: request,
      })
    }

    return documentRows
      .map((row) => ({
        ...row,
        title: displayDocumentTitle(row.title, row.source_uri, row.document_id),
      }))
      .sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''))
  }, [documents, pendingByDocumentId, pendingRequests])

  const filteredRows = useMemo(() => {
    if (statusFilter === 'pending') {
      return rows.filter((row) => row.curation?.status === 'pending')
    }
    return rows
  }, [rows, statusFilter])

  const loadPendingRequests = useCallback(async () => {
    // BFF GET /curation/requests は rag:approve のみ。member(curate) が呼ぶと 403。
    if (!canApprove) {
      setPendingRequests([])
      return
    }

    try {
      const response = await listCurationRequests('pending')
      setPendingRequests(response.requests)
    } catch (err) {
      setPendingRequests([])
      setError(
        err instanceof MaruyaApiError
          ? (err.body.message ?? err.message)
          : '承認待ち一覧の取得に失敗しました',
      )
    }
  }, [canApprove])

  const loadDocuments = useCallback(async (cursor?: string) => {
    const isMore = Boolean(cursor)
    if (isMore) {
      setLoadingMore(true)
    } else {
      setLoading(true)
    }
    setError(null)

    try {
      const response = await listDocuments({ cursor, limit: 50 })
      setDocuments((current) =>
        isMore ? [...current, ...response.documents] : response.documents,
      )
      setNextCursor(response.next_cursor)
      setDocumentsLoaded(true)
    } catch (err) {
      setError(
        err instanceof MaruyaApiError
          ? (err.body.message ?? err.message)
          : '一覧の取得に失敗しました',
      )
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }, [])

  useEffect(() => {
    if (!canWorkbench) {
      setLoading(false)
      return
    }

    let cancelled = false
    async function bootstrap() {
      setLoading(true)
      setError(null)
      try {
        await loadPendingRequests()
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }
    void bootstrap()
    return () => {
      cancelled = true
    }
  }, [canWorkbench, loadPendingRequests])

  useEffect(() => {
    if (!canWorkbench || statusFilter !== 'all' || documentsLoaded) {
      return
    }
    void loadDocuments()
  }, [canWorkbench, statusFilter, documentsLoaded, loadDocuments])

  useEffect(() => {
    const deleteId = searchParams.get('delete')
    if (!deleteId || !canCurate) {
      return
    }

    let cancelled = false
    async function openDeleteDialog() {
      try {
        const detail = await getDocument(deleteId)
        if (cancelled) {
          return
        }
        if (pendingByDocumentId.has(deleteId)) {
          setSearchParams({}, { replace: true })
          return
        }
        setDeleteTarget({
          document_id: detail.document_id,
          source_uri: detail.source_uri,
          title: displayDocumentTitle(
            detail.title,
            detail.source_uri,
            detail.document_id,
          ),
          updated_at: detail.updated_at,
          source_type: detail.source_type,
          curation: null,
        })
      } catch {
        // 詳細取得失敗時はダイアログを開かない
      } finally {
        if (!cancelled) {
          setSearchParams({}, { replace: true })
        }
      }
    }

    void openDeleteDialog()
    return () => {
      cancelled = true
    }
  }, [canCurate, pendingByDocumentId, searchParams, setSearchParams])

  async function refreshAfterCuration() {
    await loadPendingRequests()
    if (statusFilter === 'all' || documentsLoaded) {
      await loadDocuments()
    }
  }

  async function runApprovalAction() {
    if (!approvalAction) {
      return
    }
    const { documentId, requestId, type } = approvalAction
    setBusyDocumentId(documentId)
    setApprovalError(null)
    try {
      if (type === 'approve') {
        await approveCurationRequest(requestId)
      } else {
        await rejectCurationRequest(requestId, '')
      }
      setApprovalAction(null)
      await refreshAfterCuration()
    } catch (err) {
      setApprovalError(
        err instanceof MaruyaApiError
          ? err.message
          : type === 'approve'
            ? '承認に失敗しました'
            : '却下に失敗しました',
      )
    } finally {
      setBusyDocumentId(null)
    }
  }

  if (!canWorkbench) {
    return (
      <div className="page page-wide">
        <section className="card">
          <div className="empty-state">
            <span className="empty-state-icon">
              <IconInbox />
            </span>
            <p>
              承認待ちは担当者向けの作業台です。ナレッジを探すには AI
              質問をご利用ください。
            </p>
            <Link to="/rag/chat" className="btn btn-primary">
              <IconChat />
              AI 質問へ
            </Link>
          </div>
        </section>
      </div>
    )
  }

  return (
    <div className="page page-wide">
      <p className="lede page-intro">
        削除申請の確認と承認を行う作業台です。探す操作と削除申請は AI
        質問の根拠ナレッジから行ってください。全件一覧は「すべて」から明示的に切り替えます。
      </p>

      {error && <p className="form-error">{error}</p>}

      <section className="card documents-toolbar">
        <div className="segmented" role="tablist" aria-label="表示フィルター">
          <button
            type="button"
            role="tab"
            aria-selected={statusFilter === 'pending'}
            className={`segmented-btn${statusFilter === 'pending' ? ' is-active' : ''}`}
            onClick={() => setStatusFilter('pending')}
          >
            削除申請中
            {canApprove && pendingRequests.length > 0
              ? ` (${pendingRequests.length})`
              : ''}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={statusFilter === 'all'}
            className={`segmented-btn${statusFilter === 'all' ? ' is-active' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            すべて
          </button>
        </div>
        {pendingRequests.length > 0 && canApprove && statusFilter === 'pending' && (
          <p className="muted documents-toolbar-note">
            承認待ち {pendingRequests.length} 件 — 対象行の「承認」「却下」から処理してください。
          </p>
        )}
      </section>

      {loading ? (
        <p className="muted">読み込み中…</p>
      ) : filteredRows.length === 0 ? (
        <section className="card">
          <div className="empty-state">
            <span className="empty-state-icon">
              <IconInbox />
            </span>
            <p>
              {statusFilter === 'pending'
                ? canApprove
                  ? '削除申請中のナレッジはありません。'
                  : '申請中キューの閲覧は承認者向けです。削除申請は「すべて」から行えます。'
                : '表示できるナレッジがありません。'}
            </p>
          </div>
        </section>
      ) : (
        <section className="card data-table-card">
          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">タイトル</th>
                  <th scope="col">種別</th>
                  <th scope="col">更新日</th>
                  <th scope="col">操作</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => {
                  const isPending = row.curation?.status === 'pending'
                  const isBusy = busyDocumentId === row.document_id

                  return (
                    <tr
                      key={row.document_id}
                      className={isPending ? 'data-table-row--pending' : ''}
                    >
                      <td>
                        <Link
                          to={`/rag/documents/${row.document_id}`}
                          className="data-table-title"
                        >
                          {row.title}
                        </Link>
                        {isPending && (
                          <span className="status-badge pending data-table-inline-badge">
                            削除申請中
                          </span>
                        )}
                      </td>
                      <td>
                        <span className="type-badge">{rowTypeLabel(row)}</span>
                      </td>
                      <td>{formatDate(row.updated_at)}</td>
                      <td>
                        <div className="data-table-actions">
                          <Link
                            to={`/rag/documents/${row.document_id}`}
                            className="btn btn-ghost btn-sm"
                          >
                            詳細
                          </Link>
                          {canCurate && !isPending && (
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              onClick={() => setDeleteTarget(row)}
                            >
                              <IconTrash />
                              削除申請
                            </button>
                          )}
                          {canApprove && isPending && row.curation && (
                            <>
                              <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                disabled={isBusy}
                                onClick={() =>
                                  setApprovalAction({
                                    documentId: row.document_id,
                                    requestId: row.curation!.request_id,
                                    title: row.title,
                                    type: 'approve',
                                  })
                                }
                              >
                                <IconCheck />
                                承認
                              </button>
                              <button
                                type="button"
                                className="btn btn-ghost btn-sm"
                                disabled={isBusy}
                                onClick={() =>
                                  setApprovalAction({
                                    documentId: row.document_id,
                                    requestId: row.curation!.request_id,
                                    title: row.title,
                                    type: 'reject',
                                  })
                                }
                              >
                                <IconX />
                                却下
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {statusFilter === 'all' && nextCursor && (
            <div className="data-table-footer">
              <button
                type="button"
                className="btn btn-ghost"
                disabled={loadingMore}
                onClick={() => void loadDocuments(nextCursor)}
              >
                {loadingMore ? '読み込み中…' : 'さらに読み込む'}
              </button>
            </div>
          )}
        </section>
      )}

      {deleteTarget && (
        <DeleteRequestDialog
          document={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onSuccess={async () => {
            setDeleteTarget(null)
            await refreshAfterCuration()
          }}
        />
      )}

      {approvalAction && (
        <ConfirmDialog
          variant={approvalAction.type}
          title={approvalAction.type === 'approve' ? '削除の承認' : '削除申請の却下'}
          message={
            approvalAction.type === 'approve' ? (
              <>
                「{approvalAction.title}」の削除申請を承認します。承認後、このナレッジは AI
                の回答対象から完全に除外されます。よろしいですか？
              </>
            ) : (
              <>
                「{approvalAction.title}」の削除申請を却下します。ナレッジは引き続き参照対象として維持されます。よろしいですか？
              </>
            )
          }
          confirmLabel={approvalAction.type === 'approve' ? '承認する' : '却下する'}
          busy={busyDocumentId === approvalAction.documentId}
          error={approvalError}
          onCancel={() => {
            setApprovalAction(null)
            setApprovalError(null)
          }}
          onConfirm={() => void runApprovalAction()}
        />
      )}
    </div>
  )
}

function formatDate(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value || '—'
  }
  return date.toLocaleDateString('ja-JP')
}
